/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/require-await */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-use-before-define */
import {Button, Form, InputGroup} from 'react-bootstrap';
import {useNavigate, useLocation} from 'react-router-dom';
import {toast} from 'react-toastify';
import type {SubmitHandler} from 'react-hook-form';
import {useForm} from 'react-hook-form';
import {useState, type ChangeEvent, useEffect} from 'react';
import moment from 'moment-timezone';

import LayoutMain from 'components/Layouts/LayoutMain';
import {
	useDataPlanTemplateMutation,
	useUpdateDataPlanTemplateMutation,
} from 'services';
import type {ResetPeriod, DataPlanTemplate, UsageThreshold} from 'interfaces';
import {PERIOD_MAP} from 'constants/resetPeriods';

const stylePadding = {paddingLeft: '0.5rem'};

type Prop = {
	disabled: boolean;
	value: string;
};
interface TTemplate {
	unlimited: Prop;
	duration: Prop;
	durationUnit: Prop;
	totalVolume: Prop;
	downlinkVolume: Prop;
	uplinkVolume: Prop;
	totalVolumeUnit: Prop;
	downlinkVolumeUnit: Prop;
	uplinkVolumeUnit: Prop;
}

const SubscriberPlanDetail = () => {
	const navigate = useNavigate();
	const location = useLocation();

	const {state} = location;
	const [isEdit, setIsEdit] = useState(false);

	useEffect(() => {
		setIsEdit(state?.name?.length > 0);
	}, [state?.name]);

	const {
		register,
		handleSubmit,
		formState: {errors},
		setValue,
		watch,
	} = useForm<DataPlanTemplate>({
		mode: 'all',
		defaultValues: defaultData({
			...state,
			usageThreshold: {
				...state?.usageThreshold,
				...convertFromMinutes(state?.usageThreshold?.duration),
			},
		}) as never,
	});

	const [inputsDisabled, setInputsDisabled] = useState({
		unlimited: {
			disabled: true,
			value: true,
		},
		duration: {
			disabled: true,
			value: true,
		},
		totalVolume: {
			disabled: true,
			value: true,
		},
		downlinkVolume: {
			disabled: true,
			value: true,
		},
		uplinkVolume: {
			disabled: true,
			value: true,
		},
		totalVolumeUnit: {
			disabled: true,
			value: true,
		},
		downlinkVolumeUnit: {
			disabled: true,
			value: true,
		},
		uplinkVolumeUnit: {
			disabled: true,
			value: true,
		},
	});

	const [mode, setMode] = useState(() => {
		if (state?.resetPeriod) {
			if (state.resetPeriod.maxNumPeriod === 1) return 'One time';
			if (state.resetPeriod.maxNumPeriod === 0) return 'Recurrent Unlimited';
			return 'Recurrent';
		}
		return '';
	});

	const [resetPeriodValue, setResetPeriodValue] = useState('');

	useEffect(() => {
		if (state?.resetPeriod?.period) {
			setResetPeriodValue(state.resetPeriod.period);
		}
	}, [state?.resetPeriod?.period]);

	const getPeriodText = (period: string): string => {
		if (!period) return '';

		return PERIOD_MAP[period];
	};

	const isUnlimited = (usageThreshold?: UsageThreshold): boolean =>
		usageThreshold?.duration === null &&
		usageThreshold?.totalVolume === null &&
		usageThreshold?.downlinkVolume === null &&
		usageThreshold?.uplinkVolume === null;

	/* The above code is using the `useEffect` hook in a React component. It is triggered whenever the
	`state.usageThreshold` value changes. */
	useEffect(() => {
		if (state?.usageThreshold) {
			if (isUnlimited(state?.usageThreshold)) {
				setInputsDisabled(prevState => ({
					...prevState,
					unlimited: {
						disabled: false,
						value: true,
					},
				}));
			} else {
				for (const key in state?.usageThreshold) {
					setInputsDisabled(prevState => ({
						...prevState,
						[key]: {
							disabled: !state.usageThreshold[key],
							value: state.usageThreshold[key],
						},
					}));
				}
			}
		} else {
			setInputsDisabled(prevState => ({
				...prevState,
				unlimited: {
					disabled: false,
					value: true,
				},
			}));
		}
	}, [state?.usageThreshold]);

	const [saveDataPlanTemplate, {isLoading}] = useDataPlanTemplateMutation();
	const [updateDataPlanTemplate, {isLoading: isLoadingUpdate}] =
		useUpdateDataPlanTemplateMutation();

	/**
	 * The above function is a TypeScript React function that handles form submission and performs some
	 * data manipulation and API calls.
	 */
	const onSubmit: SubmitHandler<DataPlanTemplate> = async formData => {
		try {
			const tmpThreshold = {
				...formData?.usageThreshold,
				...(formData?.usageThreshold?.duration && {
					duration: convertToMinutes(
						Number(formData?.usageThreshold?.duration),
						formData?.usageThreshold?.durationUnit,
					),
				}),
			};

			delete tmpThreshold.durationUnit;

			const filteredUsageThreshold = tmpThreshold
				? Object.fromEntries(
						Object.entries(tmpThreshold as never).filter(
							([, value]) => value !== null && value !== '',
						),
				  )
				: null;

			const resetPeriod: ResetPeriod = formData.resetPeriod || {
				maxNumPeriod: 0,
				period: '',
			};

			// Set resetPeriod.period to YEARLY when mode is "One time"
			if (mode === 'One time') {
				resetPeriod.period = 'YEARLY';
				resetPeriod.maxNumPeriod = 1;
			} else if (mode === 'Recurrent Unlimited') {
				resetPeriod.maxNumPeriod = 0;
			}

			const tmp = {
				...formData,
				activationDate: moment
					.tz(formData?.activationDate, moment.tz.guess())
					.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
				expirationDate: formData?.expirationDate
					? moment
							.tz(formData?.expirationDate, moment.tz.guess())
							.format('YYYY-MM-DDTHH:mm:ss.SSSZ')
					: undefined,
				usageThreshold: filteredUsageThreshold,
				usageLevel: 'SESSION_LEVEL',
				resetPeriod: resetPeriod,
			};

			if (isEdit) {
				await updateDataPlanTemplate(tmp as never).unwrap();
			} else {
				await saveDataPlanTemplate(tmp as never).unwrap();
				setIsEdit(true);
			}

			toast.success(`${isEdit ? 'Update' : 'Create'} Successfully!`);
		} catch (error) {
			console.error(error);
		}
	};

	/**
	 * The function deactivates fields in a data object based on the provided name and value, while also
	 * enabling specific fields based on certain conditions.
	 * @param {TTemplate | any} data - The `data` parameter is of type `TTemplate` or `any`. It represents
	 * the data object that contains various fields and their properties.
	 * @param {string} name - The `name` parameter is a string that represents the name of the field that
	 * was activated or deactivated.
	 * @param {boolean} value - The `value` parameter is a boolean value that indicates whether a field
	 * should be activated or deactivated.
	 * @returns the updated `data` object with disabled fields based on the `name` and `value` parameters.
	 */
	const deactivateFieldsExceptActivated = (
		data: TTemplate | any,
		name: string,
		value: boolean,
	) => {
		let shouldEnableUplinkVolume = false;
		let shouldEnableDownlinkVolume = false;

		if (name === 'uplinkVolume' && value) {
			shouldEnableUplinkVolume = true;
			data.duration.disabled = true;
			data.unlimited.disabled = true;
			data.totalVolume.disabled = true;
			data.downlinkVolume.disabled = false;
		} else if (name === 'downlinkVolume' && value) {
			shouldEnableDownlinkVolume = true;
			data.duration.disabled = true;
			data.unlimited.disabled = true;
			data.totalVolume.disabled = true;
			data.uplinkVolume.disabled = false;
		}

		for (const key in data) {
			if (key !== name) {
				if (
					(key === 'uplinkVolume' && shouldEnableUplinkVolume) ||
					(key === 'downlinkVolume' && shouldEnableDownlinkVolume)
				) {
					data[key].disabled = false;
				} else if (!shouldEnableUplinkVolume && !shouldEnableDownlinkVolume) {
					data[key].disabled = true;
				}

				if (data[key].disabled) {
					if (key === 'unlimited') {
						data[key].value = true;
					} else {
						data[key].value = false;
					}
				} else {
					data[key].value = value;
				}
			}
		}

		return data;
	};

	/**
	 * The handleChangeDisabled function updates the state of inputsDisabled by setting the value of a
	 * specific input to either true or false.
	 * @param {string} name - A string representing the name of the input field or control that needs to
	 * be disabled or enabled.
	 * @param {boolean} value - The `value` parameter is a boolean value that indicates whether a
	 * specific input field has a value or not.
	 */
	const handleChangeDisabled = (name: string, value: boolean) => {
		if (typeof value === 'boolean') {
			setValue('usageThreshold', null);
		}

		setInputsDisabled(prevState =>
			deactivateFieldsExceptActivated(
				{
					...prevState,
					[name]: {
						disabled: typeof value === 'boolean' ? !value : !value,
						value,
					},
				},
				name,
				value,
			),
		);
	};

	const numberRegex = /^\d+$/;
	return (
		<LayoutMain isLoading={isLoading || isLoadingUpdate}>
			<div className="container-fluid">
				{/* Intro */}
				<div className="row">
					<div className="col-md-12">
						<h1 className="mt-2">Subscriber Profile Management</h1>
					</div>
				</div>

				<nav aria-label="breadcrumb">
					<ol className="breadcrumb">
						<li className="breadcrumb-item">
							<span
								role="button"
								className="text-primary"
								onClick={() => navigate(-1)}>
								Subscriber Profile Management
							</span>
						</li>
						<li className="breadcrumb-item active" aria-current="page">
							{isEdit ? 'Edit' : 'Create'}
						</li>
					</ol>
				</nav>

				{/* Subscriber Profile Management*/}
				<div className="row mt-4">
					<div className="col-md-12">
						<div className="card-tasks card">
							<div className="card-header">
								<i className="bi bi-person-lines-fill mr-3 float-left text-primary bi-2-5" />
								<h2 className="card-title">
									{isEdit ? 'Edit Plan' : 'New Plan'}
								</h2>
								<p className="card-category">
									{isEdit ? 'Edit the existent plan' : 'Create a new plan'}
								</p>
							</div>
							<div className="card-body">
								<div className="mt-1">
									<div className="row mb-5 d-flex justify-content-center align-items-center">
										<div className="col-xs-12 col-xl-8">
											<Form
												className="rounded-3 text-bg-light p-2 needs-validation"
												onSubmit={handleSubmit(onSubmit)}>
												<h2>Plan details</h2>

												<InputGroup className="col-xl-10 mt-4 mb-3">
													<InputGroup.Text style={stylePadding}>
														Name
													</InputGroup.Text>
													<Form.Control
														id="name"
														disabled={isEdit}
														aria-label="name"
														placeholder="Plan-5G-10GB"
														{...register('name', {
															required: 'The field is required',
														})}
														className={errors.name ? 'is-invalid' : ''}
													/>
													{errors.name && (
														<small className="invalid-feedback d-block">
															{errors.name.message}
														</small>
													)}
												</InputGroup>

												<InputGroup className="mb-4">
													<div className="col-xl-12 mt-1">
														<h3>Period of use</h3>
													</div>
													<div className="col-xl-5 input-group">
														<InputGroup.Text style={stylePadding}>
															Activation Date
														</InputGroup.Text>
														<Form.Control
															type="datetime-local"
															id="activationDate"
															aria-label="activationDate"
															{...register('activationDate', {
																required: 'The field is required',
															})}
															className={
																errors.activationDate ? 'is-invalid' : ''
															}
														/>
														{errors.activationDate && (
															<small className="invalid-feedback d-block">
																{errors.activationDate.message}
															</small>
														)}
													</div>
													<div className="col-xl-5 input-group">
														<InputGroup.Text
															style={{
																paddingLeft: '0.5rem',
																maxHeight: '34.39px',
															}}>
															Expiration Date
														</InputGroup.Text>
														<Form.Control
															type="datetime-local"
															id="expirationDate"
															aria-label="expirationDate"
															{...register('expirationDate')}
															className={
																errors.expirationDate ? 'is-invalid' : ''
															}
														/>
														{errors.expirationDate && (
															<small className="invalid-feedback d-block">
																{errors.expirationDate.message}
															</small>
														)}
													</div>
												</InputGroup>

												<InputGroup className="mb-4 input-group">
													<div className="col-xl-12">
														<h3>Bill Cycle</h3>
													</div>
													<div className="col-xl-4 input-group">
														<InputGroup.Text style={stylePadding}>
															Mode
														</InputGroup.Text>
														<Form.Select
															id="mode"
															aria-label="mode"
															onChange={e => setMode(e.target.value)}
															value={mode}>
															<option disabled value="" selected>
																Select option
															</option>
															<option value="One time">One time</option>
															<option value="Recurrent Unlimited">
																Recurrent Unlimited
															</option>
															<option value="Recurrent">Recurrent</option>
														</Form.Select>
													</div>
													{(mode === 'Recurrent' ||
														mode === 'Recurrent Unlimited') && (
														<div className="col-xl-4 input-group">
															<InputGroup.Text style={stylePadding}>
																Reset Period
															</InputGroup.Text>
															<Form.Select
																id="resetPeriod.period"
																aria-label="resetPeriod.period"
																{...register('resetPeriod.period', {
																	required:
																		mode === 'Recurrent' ||
																		mode === 'Recurrent Unlimited'
																			? 'This field is required'
																			: false,
																	onChange: e =>
																		setResetPeriodValue(e.target.value),
																})}
																className={
																	errors?.resetPeriod?.period
																		? 'is-invalid'
																		: ''
																}>
																<option disabled value="" selected>
																	Select option
																</option>
																<option value="YEARLY">Yearly</option>
																<option value="MONTHLY">Monthly</option>
																<option value="WEEKLY">Weekly</option>
																<option value="DAILY">Daily</option>
																<option value="HOURLY">Hourly</option>
															</Form.Select>
															{errors?.resetPeriod?.period && (
																<small className="invalid-feedback d-block">
																	{errors?.resetPeriod?.period.message}
																</small>
															)}
														</div>
													)}
													{mode === 'Recurrent' && (
														<div className="col-xl-4 input-group">
															<InputGroup.Text style={stylePadding}>
																Period of Use
															</InputGroup.Text>
															<div className="d-flex">
																<Form.Control
																	type="number"
																	id="resetPeriod.maxNumPeriod"
																	className={
																		errors?.resetPeriod?.maxNumPeriod
																			? 'is-invalid'
																			: ''
																	}
																	style={{width: '100px'}}
																	aria-label="resetPeriod.maxNumPeriod"
																	{...register('resetPeriod.maxNumPeriod', {
																		required:
																			mode === 'Recurrent' ||
																			mode === 'Recurrent Unlimited'
																				? 'This field is required'
																				: false,
																		pattern: {
																			value: numberRegex,
																			message: 'Max. Reset Times is not valid.',
																		},
																	})}
																/>
																{resetPeriodValue &&
																	Boolean(
																		watch('resetPeriod.maxNumPeriod'),
																	) && (
																		<div className="ms-2 d-flex align-items-center form-control-plaintext">
																			{getPeriodText(resetPeriodValue)}
																		</div>
																	)}
															</div>
															{errors?.resetPeriod?.maxNumPeriod && (
																<small className="invalid-feedback d-block">
																	{errors?.resetPeriod?.maxNumPeriod.message}
																</small>
															)}
														</div>
													)}
												</InputGroup>

												<InputGroup className="mb-3 input-group">
													<div className="col-xl-12">
														<h3>Usage Threshold</h3>
													</div>
													<div className="col-xl-10 input-group mb-1 bg-gray rounded ml-5 pb-2 pt-2">
														<Form.Check
															type="switch"
															id="custom-switch"
															className="ml-4 mt-2"
															checked={!inputsDisabled?.unlimited.disabled}
															onChange={(e: any) =>
																handleChangeDisabled(
																	'unlimited',
																	e.target.checked,
																)
															}
														/>
														<InputGroup.Text
															style={stylePadding}
															className="col-xl-3">
															Unlimited
														</InputGroup.Text>
														<Form.Control
															disabled={true}
															type="string"
															id="inputsDisabled.unlimited"
															aria-label="inputsDisabled.unlimited"
															className="col-xl-9"
														/>
													</div>
													<div className="col-xl-10 input-group mb-1 bg-gray rounded ml-5 pb-2 pt-2">
														<Form.Check
															type="switch"
															id="custom-switch"
															className="ml-4 mt-2"
															checked={!inputsDisabled?.duration.disabled}
															onChange={(e: any) =>
																handleChangeDisabled(
																	'duration',
																	e.target.checked,
																)
															}
														/>
														<InputGroup.Text
															style={stylePadding}
															className="col-xl-3">
															Duration
														</InputGroup.Text>
														<Form.Control
															disabled={inputsDisabled.duration.disabled}
															type="number"
															id="usageThreshold.duration"
															aria-label="usageThreshold.duration"
															{...register('usageThreshold.duration', {
																pattern: {
																	value: numberRegex,
																	message: 'Duration is not valid.',
																},
																onChange: (
																	e: ChangeEvent<HTMLInputElement>,
																) => {
																	const {value} = e.target;
																	handleChangeDisabled(
																		'duration',
																		value !== '' ? JSON.parse(value) : value,
																	);
																},
															})}
															className={
																errors?.usageThreshold?.duration
																	? 'is-invalid col-xl-6'
																	: 'col-xl-6'
															}
														/>
														{errors?.usageThreshold?.duration && (
															<small className="invalid-feedback d-block">
																{errors?.usageThreshold?.duration.message}
															</small>
														)}
														<Form.Select
															disabled={inputsDisabled.duration.disabled}
															aria-label="Gbps"
															className={
																errors.usageThreshold?.durationUnit
																	? 'is-invalid col-xl-3'
																	: 'col-xl-3'
															}
															{...register('usageThreshold.durationUnit', {
																required: !inputsDisabled.duration.disabled,
															})}>
															<option disabled value="">
																Units
															</option>
															<option value="minutes">minutes</option>
															<option value="hours">hours</option>
															<option value="days">days</option>
														</Form.Select>
													</div>
													<div className="col-xl-10 input-group mb-1 bg-gray rounded ml-5 pb-2 pt-2">
														<Form.Check
															type="switch"
															id="custom-switch"
															className="ml-4 mt-2"
															checked={!inputsDisabled?.totalVolume.disabled}
															onChange={(e: any) =>
																handleChangeDisabled(
																	'totalVolume',
																	e.target.checked,
																)
															}
														/>
														<InputGroup.Text
															style={stylePadding}
															className="col-xl-3">
															Total Volume
														</InputGroup.Text>
														<Form.Control
															disabled={inputsDisabled.totalVolume.disabled}
															type="number"
															id="usageThreshold.totalVolume"
															aria-label="usageThreshold.totalVolume"
															placeholder=""
															{...register('usageThreshold.totalVolume', {
																pattern: {
																	value: numberRegex,
																	message: 'Total Volume is not valid.',
																},
																onChange: (
																	e: ChangeEvent<HTMLInputElement>,
																) => {
																	const {value} = e.target;
																	handleChangeDisabled(
																		'totalVolume',
																		value !== '' ? JSON.parse(value) : value,
																	);
																},
															})}
															className={
																errors?.usageThreshold?.totalVolume
																	? 'is-invalid col-xl-6'
																	: 'col-xl-6'
															}
														/>
														{errors?.usageThreshold?.totalVolume && (
															<small className="invalid-feedback d-block">
																{errors?.usageThreshold?.totalVolume.message}
															</small>
														)}
														<Form.Select
															disabled={inputsDisabled.totalVolume.disabled}
															aria-label="Gbps"
															className={
																errors.usageThreshold?.totalVolumeUnit
																	? 'is-invalid col-xl-3'
																	: 'col-xl-3'
															}
															{...register('usageThreshold.totalVolumeUnit', {
																required: !inputsDisabled.totalVolume.disabled,
															})}>
															<option disabled value="">
																Units
															</option>
															<option value="b">b</option>
															<option value="Kb">Kb</option>
															<option value="Mb">Mb</option>
															<option value="Gb">Gb</option>
															<option value="Tb">Tb</option>
														</Form.Select>
													</div>

													<div className="col-xl-10 bg-gray rounded ml-5 pb-2 pt-2">
														<div className="col-xl-12 input-group mb-3 mt-2">
															<Form.Check
																type="switch"
																id="custom-switch"
																className="ml-2 mt-2"
																checked={
																	!inputsDisabled?.downlinkVolume.disabled
																}
																onChange={(e: any) =>
																	handleChangeDisabled(
																		'downlinkVolume',
																		e.target.checked,
																	)
																}
															/>
															<InputGroup.Text
																style={stylePadding}
																className="col-xl-3">
																Downlink Volume
															</InputGroup.Text>
															<Form.Control
																disabled={
																	inputsDisabled.downlinkVolume.disabled
																}
																type="number"
																id="usageThreshold.downlinkVolume"
																aria-label="usageThreshold.downlinkVolume"
																placeholder=""
																{...register('usageThreshold.downlinkVolume', {
																	pattern: {
																		value: numberRegex,
																		message: 'Downlink Volume is not valid.',
																	},
																	onChange: (
																		e: ChangeEvent<HTMLInputElement>,
																	) => {
																		const {value} = e.target;
																		handleChangeDisabled(
																			'downlinkVolume',
																			value !== '' ? JSON.parse(value) : value,
																		);
																	},
																})}
																className={
																	errors?.usageThreshold?.downlinkVolume
																		? 'is-invalid col-xl-6'
																		: 'col-xl-6'
																}
															/>
															{errors?.usageThreshold?.downlinkVolume && (
																<small className="invalid-feedback d-block">
																	{
																		errors?.usageThreshold?.downlinkVolume
																			.message
																	}
																</small>
															)}

															<Form.Select
																disabled={
																	inputsDisabled.downlinkVolume.disabled
																}
																aria-label="Gbps"
																className={
																	errors.usageThreshold?.downlinkVolumeUnit
																		? 'is-invalid col-xl-3'
																		: 'col-xl-3'
																}
																{...register(
																	'usageThreshold.downlinkVolumeUnit',
																	{
																		required:
																			!inputsDisabled.downlinkVolume.disabled,
																	},
																)}>
																<option disabled value="">
																	Units
																</option>
																<option value="b">b</option>
																<option value="Kb">Kb</option>
																<option value="Mb">Mb</option>
																<option value="Gb">Gb</option>
																<option value="Tb">Tb</option>
															</Form.Select>
														</div>
														<div className="col-xl-12 input-group mb-3">
															<div className="ml-2 mt-2 form-switch" />
															<InputGroup.Text
																style={stylePadding}
																className="col-xl-3">
																Uplink Volume
															</InputGroup.Text>
															<Form.Control
																disabled={inputsDisabled.uplinkVolume.disabled}
																type="number"
																id="usageThreshold.uplinkVolume"
																aria-label="usageThreshold.uplinkVolume"
																placeholder=""
																{...register('usageThreshold.uplinkVolume', {
																	pattern: {
																		value: numberRegex,
																		message: 'Uplink Volume is not valid.',
																	},
																	onChange: (
																		e: ChangeEvent<HTMLInputElement>,
																	) => {
																		const {value} = e.target;
																		handleChangeDisabled(
																			'uplinkVolume',
																			value !== '' ? JSON.parse(value) : value,
																		);
																	},
																})}
																className={
																	errors?.usageThreshold?.uplinkVolume
																		? 'is-invalid col-xl-6'
																		: 'col-xl-6'
																}
															/>
															{errors?.usageThreshold?.uplinkVolume && (
																<small className="invalid-feedback d-block">
																	{errors?.usageThreshold?.uplinkVolume.message}
																</small>
															)}

															<Form.Select
																disabled={inputsDisabled.uplinkVolume.disabled}
																aria-label="Gbps"
																className={
																	errors.usageThreshold?.uplinkVolumeUnit
																		? 'is-invalid col-xl-3'
																		: 'col-xl-3'
																}
																{...register(
																	'usageThreshold.uplinkVolumeUnit',
																	{
																		required:
																			!inputsDisabled.uplinkVolume.disabled,
																	},
																)}>
																<option disabled value="">
																	Units
																</option>
																<option value="b">b</option>
																<option value="Kb">Kb</option>
																<option value="Mb">Mb</option>
																<option value="Gb">Gb</option>
																<option value="Tb">Tb</option>
															</Form.Select>
														</div>
													</div>
												</InputGroup>

												<Button
													type="submit"
													className="mt-2"
													variant="primary"
													style={{color: 'white'}}>
													{isEdit ? 'Update' : 'Create'}
												</Button>
											</Form>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</LayoutMain>
	);
};

const convertToMinutes = (
	duration: number,
	unit: string | undefined,
): number => {
	let minutes = 0;

	switch (unit) {
		case 'minutes':
			minutes = duration;
			break;
		case 'hours':
			minutes = duration * 60;
			break;
		case 'days':
			minutes = duration * 60 * 24;
			break;
		default:
			throw new Error(
				'Invalid time unit. Please choose between "minutes", "hours" or "days".',
			);
	}

	return minutes;
};

const convertFromMinutes = (minutes: number) => {
	if (minutes < 0) {
		throw new Error('The duration in minutes must be a positive number.');
	}

	let duration: number | null = null;
	let durationUnit = '';

	if (minutes < 60) {
		duration = minutes;
	} else if (minutes < 1440) {
		const hours = Math.floor(minutes / 60);
		duration = hours;
		durationUnit = 'hours';
	} else if (minutes >= 1440) {
		const days = Math.floor(minutes / 1440);
		duration = days;
		durationUnit = 'days';
	}

	return {
		duration,
		durationUnit,
	};
};

export const defaultData = (state?: DataPlanTemplate) => {
	const userTimeZone = moment.tz.guess();
	return {
		name: state?.name ?? '',
		activationDate: state?.activationDate
			? moment
					.tz(state?.activationDate, userTimeZone)
					.format('YYYY-MM-DDTHH:mm')
			: undefined,
		expirationDate: state?.expirationDate
			? moment
					.tz(state?.expirationDate, userTimeZone)
					.format('YYYY-MM-DDTHH:mm')
			: undefined,
		resetPeriod: state?.resetPeriod || null,
		usageThreshold: state?.usageThreshold || null,
		usageLevel: state?.usageThreshold || '',
		scopes: state?.scopes || null,
	};
};

export default SubscriberPlanDetail;
